import { useState } from "react";

/* PACKAGES */
import { motion } from "framer-motion";
import { ToastContainer } from "react-toastify";
import { PopupModal } from "react-calendly";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* ANIMATION */
import { animation, containerAnimation, GCP_BUCKET_URL } from "config";

/* COMPONENTS */
import {
  CallToAction,
  CounterComponent,
  FrequentlyAskedQuestions,
} from "components/homepage";

/* ICONS */
import { FaCircleCheck } from "react-icons/fa6";

const EmployeeEngagementPage = () => {
  const [calendlyModal, setCalendlyModal] = useState(false);

  return (
    <motion.section
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="bg-transparent"
    >
      {/* TOAST CONTAINER */}
      <ToastContainer theme="colored" closeOnClick autoClose={5000} />

      {/* BANNER */}
      <motion.div
        variants={animation}
        className="p-0 py-5 xl:p-5 lg:p-5 relative h-auto xl:h-featureBanner lg:h-featureBanner"
      >
        {/* LEFT SECTION */}
        <div className="container mx-auto h-full">
          <div className="h-full flex items-center">
            <div className="w-full xl:w-2/5 lg:w-2/5">
              <div className="bg-brandColor bg-opacity-10 rounded-full border border-brandColor w-fit px-4 py-1">
                <p className="text-sm font-asap font-bold text-brandColor">
                  AI-Driven Employee Engagement
                </p>
              </div>
              <div className="mt-5">
                <h1 className="text-3xl xl:text-4xl lg:text-4xl font-asap font-bold text-black leading-snug">
                  AI-Driven Employee Engagement: Stop Guessing. Start
                  Predicting.
                </h1>
                <h2 className="mt-5 text-lg xl:text-xl lg:text-xl font-asap font-normal text-greyText">
                  True engagement isn’t about sending a survey once a quarter.
                  It’s about specific, actionable data. Aligned Rewards uses
                  Agentic AI to detect early signals of burnout and friction
                  before they become resignation letters.
                </h2>
                <div className="mt-5 xl:mt-8 lg:mt-8">
                  <div className="flex items-center gap-3">
                    <Button
                      className="shadow-none hover:shadow-none rounded-lg bg-brandColor normal-case text-base font-asap font-bold text-white hover:bg-opacity-80 transition-all duration-300 ease-in-out"
                      onClick={() => setCalendlyModal(!calendlyModal)}
                    >
                      Schedule a Demo
                    </Button>
                  </div>
                </div>
                <div className="mt-8">
                  <div className="flex items-center gap-2">
                    <FaCircleCheck className="w-5 h-5 text-green-500" />
                    <p className="text-base font-asap font-normal text-greyText">
                      30 day free trial
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <FaCircleCheck className="w-5 h-5 text-green-500" />
                    <p className="text-base font-asap font-normal text-greyText">
                      No credit card required
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SECTION */}
        <div className="relative xl:absolute lg:absolute top-5 right-0 w-full xl:w-1/2 lg:w-1/2 h-full overflow-hidden pl-5 xl:pl-0 lg:pl-0">
          <div className="h-full bg-gradient-to-tr from-blue-100 to-white rounded-l-2xl ">
            <div className="w-[150vh] h-[40vh] xl:h-featureBanner lg:h-featureBanner relative xl:absolute lg:absolute top-0 xl:top-5 lg:top-5 left-0 xl:left-5 lg:left-5">
              <img
                src={`${GCP_BUCKET_URL}/rename-employee-development-02.png`}
                className="w-full h-[100vh] rounded-tl-xl"
                alt="Boost morale, productivity, and collaboration with our Employee Engagement Software"
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* COUNTERS */}
      <motion.div variants={animation} className="mt-5 xl:mt-10 lg:mt-10">
        <div className="bg-footer bg-opacity-5 py-10">
          <div className="container mx-auto">
            <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-10 xl:gap-20 lg:gap-20">
              <div className="col-span-1">
                <CounterComponent
                  title="Predict Retention Risks Body"
                  description="Don't wait for the exit interview. Our Predictive Analytics flag high-risk employees by analyzing engagement drops in real-time, allowing you to retain top talent."
                />
              </div>
              <div className="col-span-1">
                <CounterComponent
                  title="Automate Culture Nudges"
                  description="Reduce HR admin by 40%. Our AI Agent automatically prompts managers to celebrate wins, schedule check-ins, and approve leave, so culture happens on autopilot."
                />
              </div>
              <div className="col-span-1">
                <CounterComponent
                  title="Align Strategy to Execution"
                  description="Simplify task management by linking every daily to-do list directly to company-wide goals. Employees stay engaged because they see exactly how their work matters."
                />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* FEATURE 01 */}
      <motion.div variants={animation} className="py-5 xl:py-14 lg:py-14">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-y-5 xl:gap-10 lg:gap-10 items-center">
            <div className="col-span-1 order-2 xl:order-1 lg:order-1">
              <div className="w-full h-[50vh] overflow-hidden shadow-md rounded-xl">
                <div className="w-full h-full rounded-xl bg-gradient-to-br from-blue-100 to-white relative">
                  <div className="w-[85vh] h-auto relative xl:absolute lg:absolute top-5 xl:top-14 lg:top-14 -left-5 xl:left-5 lg:left-5">
                    <img
                      src={`${GCP_BUCKET_URL}/employee-engagement/02.png`}
                      className="w-full h-[55vh] rounded-lg"
                      alt="Harmonize your teams with objectives for enhanced outcomes"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-span-1 order-1 xl:order-2 lg:order-2 flex items-center justify-center">
              <div className="w-full xl:w-11/12 lg:w-11/12">
                <h2 className="text-2xl xl:text-3xl lg:text-3xl font-asap font-bold text-black leading-snug">
                  Turn "Transactional Perks" into "Relational Rewards"
                </h2>
                <h5 className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText mt-3">
                  Stop wasting budget on generic gift cards. Our AI-Driven
                  Rewards Engine analyzes individual preferences to suggest
                  rewards that actually drive loyalty, including time off and
                  custom experiences.
                </h5>
                <div className="mt-5">
                  <div className="flex items-start gap-3">
                    <FaCircleCheck className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <p className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText">
                      <span className="font-semibold text-black">
                        Gamify Performance:
                      </span>{" "}
                      Establish a merit-based culture where points are awarded
                      automatically for hitting milestones, not just for
                      "participation."
                    </p>
                  </div>
                  <div className="flex items-start gap-3 mt-3">
                    <FaCircleCheck className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <p className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText">
                      <span className="font-semibold text-black">
                        Hyper-Personalization:
                      </span>{" "}
                      Our system learns what motivates each employee (Autonomy?
                      Mastery? Purpose?) and tailors the recognition
                      accordingly.
                    </p>
                  </div>
                </div>
                <div className="mt-5 xl:mt-10 lg:mt-10">
                  <Button
                    className="shadow-none hover:shadow-none rounded-lg bg-brandColor normal-case text-base font-asap font-bold text-white hover:bg-opacity-80 transition-all duration-300 ease-in-out"
                    onClick={() => setCalendlyModal(!calendlyModal)}
                  >
                    See the rewards engine
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* FEATURE 02 */}
      <motion.div
        variants={animation}
        className="py-5 xl:py-14 lg:py-14 bg-footer bg-opacity-5"
      >
        <div className="container mx-auto">
          <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-y-5 xl:gap-10 lg:gap-10 items-center">
            <div className="col-span-1 flex items-center justify-center">
              <div className="w-full xl:w-11/12 lg:w-11/12">
                <h2 className="text-2xl xl:text-3xl lg:text-3xl font-asap font-bold text-black leading-snug">
                  Eliminate "Status Meetings" with Asynchronous Alignment
                </h2>
                <h5 className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText mt-3">
                  Your team shouldn't meet to update; they should meet to solve.
                  Our platform integrates calendars with project boards,
                  ensuring that 80% of "syncs" are handled by the dashboard.
                </h5>
                <div className="mt-5">
                  <div className="flex items-start gap-3">
                    <FaCircleCheck className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <p className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText">
                      <span className="font-semibold text-black">
                        Smart Scheduling:
                      </span>{" "}
                      Integrate with Google/Outlook to find the perfect focus
                      blocks for deep work, reducing "Context Switching"
                      fatigue.
                    </p>
                  </div>
                  <div className="flex items-start gap-3 mt-5">
                    <FaCircleCheck className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <p className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText">
                      <span className="font-semibold text-black">
                        AI Agenda Builder:
                      </span>{" "}
                      The system automatically pulls overdue tasks and blockers
                      into the meeting agenda, so you never have a wasted
                      conversation.
                    </p>
                  </div>
                </div>
                <div className="mt-5 xl:mt-10 lg:mt-10">
                  <Button
                    className="shadow-none hover:shadow-none rounded-lg bg-brandColor normal-case text-base font-asap font-bold text-white hover:bg-opacity-80 transition-all duration-300 ease-in-out"
                    onClick={() => setCalendlyModal(!calendlyModal)}
                  >
                    Optimize your meetings
                  </Button>
                </div>
              </div>
            </div>
            <div className="col-span-1">
              <div className="w-full h-[50vh] overflow-hidden shadow-md rounded-xl">
                <div className="w-full h-full rounded-xl bg-gradient-to-br from-blue-100 to-white relative">
                  <div className="w-[85vh] h-auto relative xl:absolute lg:absolute top-5 xl:top-14 lg:top-14 -left-5 xl:left-5 lg:left-5">
                    <img
                      src={`${GCP_BUCKET_URL}/employee-engagement/01.png`}
                      className="w-full h-[55vh] rounded-lg"
                      alt=" Get the overall statistics of your objectives and key results at one place"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* FEATURE 01 */}
      <motion.div variants={animation} className="py-5 xl:py-14 lg:py-14">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-y-5 xl:gap-10 lg:gap-10 items-center">
            <div className="col-span-1">
              <div className="w-full h-[50vh] overflow-hidden shadow-md rounded-xl">
                <div className="w-full h-full rounded-xl bg-gradient-to-br from-blue-100 to-white relative">
                  <div className="w-[85vh] h-auto relative xl:absolute lg:absolute top-5 xl:top-14 lg:top-14 left-5 xl:left-12 lg:left-12">
                    <img
                      src={`${GCP_BUCKET_URL}/okr-management/01.png`}
                      className="w-full h-[55vh] rounded-lg"
                      alt=" Get the overall statistics of your objectives and key results at one place"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-span-1 flex items-center justify-center">
              <div className="w-full xl:w-11/12 lg:w-11/12">
                <h2 className="text-2xl xl:text-3xl lg:text-3xl font-asap font-bold text-black leading-snug">
                  Connect Daily Tasks to Company Goals (OKRs)
                </h2>
                <h5 className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText mt-3">
                  The #1 cause of disengagement is lack of purpose. Our Live OKR
                  Dashboard visualizes exactly how an employee's daily grind
                  contributes to the company's annual revenue.
                </h5>
                <div className="mt-5">
                  <div className="flex items-start gap-3">
                    <FaCircleCheck className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <p className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText">
                      <span className="font-semibold text-black">
                        Real-Time Visibility:
                      </span>{" "}
                      Create a transparent culture where every team member can
                      see their impact on the "North Star" metrics instantly.
                    </p>
                  </div>
                  <div className="flex items-start gap-3 mt-5">
                    <FaCircleCheck className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <p className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText">
                      <span className="font-semibold text-black">
                        Performance Clarity:
                      </span>{" "}
                      Move from subjective reviews to data-driven growth. Track
                      progress on Key Results automatically without manual
                      reporting.
                    </p>
                  </div>
                </div>
                <div className="mt-5 xl:mt-10 lg:mt-10">
                  <Button
                    className="shadow-none hover:shadow-none rounded-lg bg-brandColor normal-case text-base font-asap font-bold text-white hover:bg-opacity-80 transition-all duration-300 ease-in-out"
                    onClick={() => setCalendlyModal(!calendlyModal)}
                  >
                    Align your goals
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* FREQUENTLY ASKED QUESTIONS */}
      <FrequentlyAskedQuestions />

      {/* CALL TO ACTION */}
      <CallToAction />

      {/* CALENDLY MODAL */}
      <PopupModal
        open={calendlyModal}
        onModalClose={() => setCalendlyModal(!calendlyModal)}
        rootElement={document.getElementById("root")}
        url="https://calendly.com/contact-ucp8/30min"
      />
    </motion.section>
  );
};

export default EmployeeEngagementPage;
